import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { jurisdictionsApi } from "../../api/jurisdictionApi";
import type { Jurisdiction } from "../../types/jurisdiction.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

interface JurisdictionsState {
  items: Jurisdiction[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: JurisdictionsState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const jurisdictionsSlice = createSlice({
  name: "jurisdictions",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setJurisdictions: (state, action: PayloadAction<Jurisdiction[]>) => {
      state.items = action.payload;
    },
    appendJurisdictions: (state, action: PayloadAction<Jurisdiction[]>) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleJurisdictionLocally: (
      state,
      action: PayloadAction<Jurisdiction>,
    ) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetJurisdictionList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleJurisdiction: (
      state,
      action: PayloadAction<Partial<Jurisdiction> & { id: string }>,
    ) => {
      const index = state.items.findIndex((ct) => ct.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleJurisdiction: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((ct) => ct.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setJurisdictions,
  appendJurisdictions,
  addSingleJurisdictionLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetJurisdictionList,
  updateSingleJurisdiction,
  removeSingleJurisdiction,
} = jurisdictionsSlice.actions;

interface FetchJurisdictionsPayload {
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  sortAscending?: boolean;
  groupIds?: string[];
  ids?: string[];
  searchText?: string;
  includeDeleted?: boolean;
  /** Dropdowns pass true so deactivated entries can't be picked;
   *  the listing filter passes null for "show both". */
  isActive?: boolean | null;
}

export const fetchJurisdictions = (payload: FetchJurisdictionsPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().jurisdictions;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        // This endpoint reads the flag as "ascending" despite its name — a
        // quirk every dropdown in the app relies on, so it is matched here
        // rather than changed there.
        sortDecending: payload.sortAscending ?? false,
        groupIds: payload.groupIds || [userData?.companyId],
        ids: payload.ids,
        searchText: payload.searchText || "",
        includeDeleted: payload.includeDeleted || false,
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
      };

      const response = await jurisdictionsApi.searchJurisdictions(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setJurisdictions(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendJurisdictions(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Jurisdictions error:", error);
      toast.error(
        i18n.t("jurisdictions:jurisdiction.messages.fetchError", {
          defaultValue: "Failed to fetch jurisdictions",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addJurisdiction = (data: { jurisdiction: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const payload = {
        jurisdictionName: data.jurisdiction,
      };

      const res = await jurisdictionsApi.createJurisdiction(payload);

      const createdId = res?.data?.id || res?.data || res?.id || res;

      const newJurisdiction: Jurisdiction = {
        jurisdictionName: data.jurisdiction,
        id: typeof createdId === "string" ? createdId : createdId?.id,
        groupId: res?.data?.groupId || res?.groupId || "",
      };

      dispatch(addSingleJurisdictionLocally(newJurisdiction));

      toast.success(
        i18n.t("jurisdictions:jurisdiction.messages.addSuccess", {
          defaultValue: "Jurisdiction created successfully",
        }),
      );
      return newJurisdiction;
    } catch (error) {
      console.error("Create Jurisdiction error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("jurisdictions:jurisdiction.messages.addError", {
          defaultValue: "Failed to create jurisdiction",
        })));
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editJurisdiction = (data: {
  id: string;
  jurisdiction: string;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));

      const payload = {
        id: data.id,
        jurisdictionName: data.jurisdiction,
      };

      const res = await jurisdictionsApi.updateJurisdiction(payload);
      const updatedType = res?.data || payload;

      dispatch(updateSingleJurisdiction(updatedType));

      toast.success(
        i18n.t("jurisdictions:jurisdiction.messages.editSuccess", {
          defaultValue: "Jurisdiction updated successfully",
        }),
      );
      return updatedType;
    } catch (error) {
      console.error("Edit Jurisdiction error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("jurisdictions:jurisdiction.messages.editError", {
          defaultValue: "Failed to update jurisdiction",
        })));
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeJurisdiction = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleJurisdiction(id));

      await jurisdictionsApi.removeJurisdiction(id);

      toast.success(
        i18n.t("jurisdictions:jurisdiction.messages.removeSuccess", {
          defaultValue: "Jurisdiction removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Jurisdiction error:", error);
      // The API refuses deletion while clauses still use the
      // record and says which — show that reason, not a generic failure.
      const fallback = i18n.t("jurisdictions:jurisdiction.messages.removeError", {
        defaultValue: "Failed to remove jurisdiction",
      });
      reportDeleteFailure(error, fallback);
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default jurisdictionsSlice.reducer;

export const activateJurisdiction = (id: string) => {
  return async (dispatch: AppDispatch) => {
    // Optimistic, then rolled back on failure — same shape as the
    // department/business-unit toggles.
    dispatch(updateSingleJurisdiction({ id, isActive: true } as any));

    try {
      await jurisdictionsApi.activateJurisdiction(id);

      toast.success(
        i18n.t("jurisdictions:jurisdiction.messages.activateSuccess", {
          defaultValue: "Jurisdiction activated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleJurisdiction({ id, isActive: false } as any));
      console.error("Activate Jurisdiction error:", error);
      toast.error(
        i18n.t("jurisdictions:jurisdiction.messages.activateError", {
          defaultValue: "Failed to activate jurisdiction",
        }),
      );
    }
  };
};

export const deactivateJurisdiction = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(updateSingleJurisdiction({ id, isActive: false } as any));

    try {
      await jurisdictionsApi.deactivateJurisdiction(id);

      toast.success(
        i18n.t("jurisdictions:jurisdiction.messages.deactivateSuccess", {
          defaultValue: "Jurisdiction deactivated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleJurisdiction({ id, isActive: true } as any));
      console.error("Deactivate Jurisdiction error:", error);
      toast.error(
        i18n.t("jurisdictions:jurisdiction.messages.deactivateError", {
          defaultValue: "Failed to deactivate jurisdiction",
        }),
      );
    }
  };
};
